import React, { useEffect, useMemo, useState } from "react";
import {
  Bell,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { AppCard } from "@/components/daily/CompanionAppUi";
import CalendarProgramFields from "@/components/daily/CalendarProgramFields";
import {
  CALENDAR_EVENT_TYPES,
  REMINDER_OPTIONS,
  USUAL_EFFORT_OPTIONS,
  WORKOUT_MODALITIES,
  buildScheduledActivityGuidance,
  createUserCalendarEvent,
  downloadEventIcs,
  expandCalendarEvents,
  requestCalendarNotifications,
  saveUserCalendarEvents,
  scheduleOpenAppReminders,
} from "@/components/daily/companionCalendarV2";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const pad = (value) => String(value).padStart(2, "0");
const dateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const emptyForm = (date = new Date()) => ({
  title: "",
  date: dateKey(date),
  time: "18:00",
  durationMinutes: 60,
  category: "workout",
  modality: "strength",
  usualEffort: "challenging",
  familiarity: "familiar",
  socialFormat: "solo",
  competitionContext: "none",
  recoveryCost: "moderate",
  modifiable: "yes",
  location: "",
  equipment: "",
  repeat: "none",
  weekdays: [DAY_CODES[date.getDay()]],
  reminderMinutes: 30,
  description: "",
});

const fieldClass =
  "mt-2 block w-full rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm forr-text-ink focus:outline-none focus:ring-2 focus:ring-teal-500/30";

const eventColor = (event) => {
  if (event.source === "assessment-plan") return "var(--forr-teal)";
  if (event.source === "assessment-scheduled") return "var(--forr-schedule)";
  if (event.category === "recovery") return "#7c88a5";
  if (event.category === "sport") return "#557d5f";
  return "var(--forr-teal-deep)";
};

const CompanionCalendar = ({
  assessment,
  plan,
  checkIn,
  identity,
  allEvents,
  userEvents,
  onUserEventsChange,
}) => {
  const [month, setMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => dateKey(new Date()));
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(new Date()));
  const [notificationState, setNotificationState] = useState(() =>
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );

  const monthEvents = useMemo(
    () => expandCalendarEvents(allEvents, month),
    [allEvents, month],
  );
  const eventsByDate = useMemo(() => {
    const map = new Map();
    monthEvents.forEach((event) => {
      const current = map.get(event.date) || [];
      map.set(event.date, [...current, event]);
    });
    return map;
  }, [monthEvents]);
  const selectedEvents = eventsByDate.get(selectedDate) || [];
  const selectedEvent =
    selectedEvents.find((event) => event.id === selectedEventId) ||
    selectedEvents[0] ||
    null;
  const guidance = selectedEvent
    ? buildScheduledActivityGuidance({
        event: selectedEvent,
        plan,
        checkIn,
        identity,
      })
    : null;

  useEffect(() => {
    const cleanup = scheduleOpenAppReminders(monthEvents);
    return cleanup;
  }, [monthEvents, notificationState]);

  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = Array.from(
    { length: firstWeekday + daysInMonth },
    (_, index) => {
      if (index < firstWeekday) return null;
      return new Date(year, monthIndex, index - firstWeekday + 1);
    },
  );

  const closeForm = () => {
    setShowForm(false);
    setEditingEventId(null);
  };

  const moveMonth = (offset) => {
    const next = new Date(year, monthIndex + offset, 1);
    setMonth(next);
    setSelectedDate(dateKey(next));
    setSelectedEventId(null);
  };

  const openAdd = (date = selectedDate) => {
    const [eventYear, eventMonth, eventDay] = date.split("-").map(Number);
    const selected = new Date(eventYear, eventMonth - 1, eventDay);
    setEditingEventId(null);
    setForm(emptyForm(selected));
    setShowForm(true);
  };

  const openEdit = (event) => {
    const baseId = event.recurringId || event.id;
    const stored = userEvents.find((item) => item.id === baseId);
    if (!stored) return;
    setEditingEventId(baseId);
    setForm({
      title: stored.title,
      date: stored.date,
      time: stored.time,
      durationMinutes: stored.durationMinutes,
      category: stored.category,
      modality: stored.modality,
      usualEffort: stored.usualEffort,
      familiarity: stored.familiarity,
      socialFormat: stored.socialFormat,
      competitionContext: stored.competitionContext,
      recoveryCost: stored.recoveryCost,
      modifiable: stored.modifiable,
      location: stored.location || "",
      equipment: stored.equipment || "",
      repeat: stored.repeat,
      weekdays: [...(stored.weekdays || [])],
      reminderMinutes: stored.reminderMinutes,
      description: stored.description || "",
    });
    setShowForm(true);
  };

  const saveEvent = (event) => {
    event.preventDefault();
    const next = createUserCalendarEvent({
      ...form,
      ...(editingEventId ? { id: editingEventId } : {}),
    });
    const nextEvents = editingEventId
      ? userEvents.map((item) => (item.id === editingEventId ? next : item))
      : [...userEvents, next];
    const saved = saveUserCalendarEvents(nextEvents);
    onUserEventsChange(saved);
    setSelectedDate(next.date);
    setSelectedEventId(next.id);
    closeForm();
  };

  const removeEvent = (event) => {
    const recurringId = event.recurringId || event.id;
    const saved = saveUserCalendarEvents(
      userEvents.filter((item) => item.id !== recurringId),
    );
    onUserEventsChange(saved);
    setSelectedEventId(null);
  };

  const enableNotifications = async () => {
    const permission = await requestCalendarNotifications();
    setNotificationState(permission);
  };

  return (
    <section data-testid="companion-calendar" className="mt-5">
      <AppCard>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="forr-overline">APOS calendar</div>
            <h2 className="mt-2 font-display text-2xl forr-text-ink">
              {new Intl.DateTimeFormat(undefined, {
                month: "long",
                year: "numeric",
              }).format(month)}
            </h2>
            <p className="mt-2 text-sm leading-relaxed forr-text-soft">
              Assessment sessions, your own workouts, sports, recovery routines,
              and guidance matched to your current nervous-system state live together here.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={enableNotifications}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs forr-btn-secondary"
            >
              <Bell size={14} />
              {notificationState === "granted"
                ? "Reminders enabled"
                : notificationState === "denied"
                  ? "Reminders blocked"
                  : notificationState === "unsupported"
                    ? "Use device calendar reminders"
                    : "Enable reminders"}
            </button>
            <button
              type="button"
              onClick={() => openAdd(selectedDate)}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs forr-btn-primary"
            >
              <Plus size={14} /> Add activity
            </button>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => moveMonth(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full forr-surface-tint"
            aria-label="Previous month"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => {
              const today = new Date();
              setMonth(today);
              setSelectedDate(dateKey(today));
            }}
            className="rounded-full px-4 py-2 text-xs forr-text-teal-deep"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => moveMonth(1)}
            className="flex h-10 w-10 items-center justify-center rounded-full forr-surface-tint"
            aria-label="Next month"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="mt-5 grid grid-cols-7 gap-1 text-center">
          {DAY_LABELS.map((label) => (
            <div
              key={label}
              className="py-2 text-[9px] uppercase tracking-[0.12em] forr-text-mute"
            >
              {label.slice(0, 2)}
            </div>
          ))}
          {cells.map((date, index) => {
            if (!date) return <div key={`blank-${index}`} />;
            const key = dateKey(date);
            const events = eventsByDate.get(key) || [];
            const selected = selectedDate === key;
            const today = key === dateKey(new Date());
            return (
              <button
                type="button"
                key={key}
                onClick={() => {
                  setSelectedDate(key);
                  setSelectedEventId(null);
                }}
                className="min-h-[66px] rounded-xl p-1.5 text-left transition-colors"
                style={{
                  backgroundColor: selected
                    ? "var(--forr-teal-tint)"
                    : today
                      ? "var(--forr-canvas-warm)"
                      : "transparent",
                  border: selected
                    ? "1px solid var(--forr-teal)"
                    : "1px solid transparent",
                }}
              >
                <span className="block text-xs font-medium forr-text-ink">
                  {date.getDate()}
                </span>
                <span className="mt-2 flex flex-wrap gap-1">
                  {events.slice(0, 3).map((event) => (
                    <span
                      key={event.id}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: eventColor(event) }}
                    />
                  ))}
                  {events.length > 3 && (
                    <span className="text-[8px] forr-text-mute">
                      +{events.length - 3}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <div
          className="mt-6 border-t pt-5"
          style={{ borderColor: "var(--forr-line-soft)" }}
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="forr-overline">
                {new Date(`${selectedDate}T12:00:00`).toLocaleDateString(
                  undefined,
                  {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  },
                )}
              </div>
              <h3 className="mt-1 font-display text-xl forr-text-ink">
                {selectedEvents.length} scheduled item
                {selectedEvents.length === 1 ? "" : "s"}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => openAdd(selectedDate)}
              className="inline-flex items-center gap-2 text-xs font-medium forr-text-teal-deep"
            >
              <CalendarPlus size={15} /> Add
            </button>
          </div>

          {selectedEvents.length ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                {selectedEvents.map((event) => (
                  <button
                    type="button"
                    key={event.id}
                    onClick={() => setSelectedEventId(event.id)}
                    className="w-full rounded-2xl p-4 text-left"
                    style={{
                      backgroundColor:
                        selectedEvent?.id === event.id
                          ? "var(--forr-teal-tint)"
                          : "var(--forr-surface-tint)",
                      border: `1px solid ${
                        selectedEvent?.id === event.id
                          ? "var(--forr-teal)"
                          : "var(--forr-line-soft)"
                      }`,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium forr-text-ink">
                          {event.title}
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs forr-text-mute">
                          <Clock3 size={12} /> {event.time} ·{" "}
                          {event.durationMinutes} min
                        </div>
                      </div>
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: eventColor(event) }}
                      />
                    </div>
                  </button>
                ))}
              </div>

              {selectedEvent && (
                <div className="rounded-2xl p-4 forr-panel-information">
                  <div className="text-[10px] uppercase tracking-[0.18em] forr-text-teal-deep">
                    Guidance for your current nervous-system state
                  </div>
                  <h4 className="mt-2 font-display text-xl forr-text-ink">
                    {guidance?.title || selectedEvent.title}
                  </h4>
                  <p className="mt-3 text-sm leading-relaxed forr-text-soft">
                    {guidance?.instruction ||
                      selectedEvent.description ||
                      "Complete the event as planned."}
                  </p>
                  {guidance?.reason && (
                    <p className="mt-3 text-xs leading-relaxed forr-text-mute">
                      {guidance.reason}
                    </p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        downloadEventIcs(selectedEvent, guidance)
                      }
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs forr-btn-secondary"
                    >
                      <Download size={14} /> Add to device calendar
                    </button>
                    {selectedEvent.source === "user" && (
                      <>
                        <button
                          type="button"
                          onClick={() => openEdit(selectedEvent)}
                          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs forr-btn-secondary"
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => removeEvent(selectedEvent)}
                          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs text-red-700"
                          style={{
                            border: "1px solid rgba(185, 60, 60, 0.25)",
                          }}
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl p-5 forr-surface-tint">
              <p className="text-sm forr-text-soft">
                Nothing is scheduled for this day.
              </p>
            </div>
          )}
        </div>
      </AppCard>

      {showForm && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 p-0 sm:items-center sm:p-6">
          <form
            onSubmit={saveEvent}
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-[28px] bg-[#fbfaf6] p-6 sm:rounded-[28px] sm:p-8"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="forr-overline">
                  {editingEventId
                    ? "Edit APOS calendar activity"
                    : "Add to APOS calendar"}
                </div>
                <h2 className="mt-2 font-display text-2xl forr-text-ink">
                  {editingEventId
                    ? "Update your activity"
                    : "Schedule your activity"}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeForm}
                className="flex h-10 w-10 items-center justify-center rounded-full forr-surface-tint"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <label className="text-sm forr-text-soft sm:col-span-2">
                Activity name
                <input
                  required
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                  placeholder="Upper body, lower body, volleyball, walking…"
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Type
                <select
                  value={form.category}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      category: event.target.value,
                    }))
                  }
                  className={fieldClass}
                >
                  {CALENDAR_EVENT_TYPES.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm forr-text-soft">
                Session format
                <select
                  value={form.modality}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      modality: event.target.value,
                    }))
                  }
                  className={fieldClass}
                >
                  {WORKOUT_MODALITIES.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm forr-text-soft">
                Date
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      date: event.target.value,
                    }))
                  }
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Time
                <input
                  type="time"
                  required
                  value={form.time}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      time: event.target.value,
                    }))
                  }
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Duration (minutes)
                <input
                  type="number"
                  min="5"
                  max="360"
                  value={form.durationMinutes}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      durationMinutes: event.target.value,
                    }))
                  }
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Normal effort
                <select
                  value={form.usualEffort}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      usualEffort: event.target.value,
                    }))
                  }
                  className={fieldClass}
                >
                  {USUAL_EFFORT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm forr-text-soft">
                Repeat
                <select
                  value={form.repeat}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      repeat: event.target.value,
                    }))
                  }
                  className={fieldClass}
                >
                  <option value="none">Does not repeat</option>
                  <option value="weekly">Weekly</option>
                </select>
              </label>
              <label className="text-sm forr-text-soft">
                Reminder
                <select
                  value={form.reminderMinutes}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      reminderMinutes: Number(event.target.value),
                    }))
                  }
                  className={fieldClass}
                >
                  {REMINDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {form.repeat === "weekly" && (
              <div className="mt-5">
                <div className="text-sm forr-text-soft">Repeat on</div>
                <div className="mt-2 grid grid-cols-7 gap-1.5">
                  {DAY_LABELS.map((label, index) => {
                    const code = DAY_CODES[index];
                    const selected = form.weekdays.includes(code);
                    return (
                      <button
                        type="button"
                        key={code}
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            weekdays: selected
                              ? current.weekdays.filter((item) => item !== code)
                              : [...current.weekdays, code],
                          }))
                        }
                        className="rounded-xl py-2 text-xs"
                        style={{
                          backgroundColor: selected
                            ? "var(--forr-teal-soft)"
                            : "var(--forr-surface)",
                          border: `1px solid ${
                            selected
                              ? "var(--forr-teal)"
                              : "var(--forr-line-soft)"
                          }`,
                        }}
                      >
                        {label.slice(0, 2)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="submit"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium forr-btn-primary"
            >
              {editingEventId ? <Pencil size={16} /> : <CalendarPlus size={16} />}
              {editingEventId ? "Save changes" : "Save activity"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
};

export default CompanionCalendar;
